import React, { useEffect, useState } from 'react';
import { getSubscriptionStatus } from '../../services/api';
import { getStoredAuth } from '../../services/nativeAppleSignIn';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Re-engagement in-app banner (dual-channel nudge, in-app half).
 *
 * On mount (and on auth change) it calls the app's launch-time endpoint
 * (GET /subscription/{userId}); if the response carries an `inAppNudge` that is
 * < 30 days old and hasn't been dismissed (localStorage['nudgeDismissed:<id>']),
 * it renders a single dismissible banner styled in the app's toast/design language.
 *
 * No new API routes — the nudge rides on the existing subscription-status call.
 */
export default function NudgeBanner() {
  const [nudge, setNudge] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const auth = getStoredAuth();
      if (!auth?.userId || !auth?.idToken) {
        if (!cancelled) setNudge(null);
        return;
      }
      try {
        const remote = await getSubscriptionStatus(auth.userId);
        if (cancelled) return;
        const n = remote?.inAppNudge;
        if (
          n?.id &&
          n.createdAt &&
          Date.now() - Number(n.createdAt) < THIRTY_DAYS_MS &&
          !localStorage.getItem(`nudgeDismissed:${n.id}`)
        ) {
          setNudge(n);
        } else {
          setNudge(null);
        }
      } catch {
        // silent — a missing nudge must never surface an error
      }
    };

    load();
    const handler = () => load();
    window.addEventListener('pomogrow:auth-change', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('pomogrow:auth-change', handler);
    };
  }, []);

  if (!nudge) return null;

  const dismiss = () => {
    try { localStorage.setItem(`nudgeDismissed:${nudge.id}`, '1'); } catch { /* ignore */ }
    setNudge(null);
  };

  return (
    <div
      className="px-4 pt-2"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
    >
      <div
        role="status"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          maxWidth: 'var(--content-max-width)',
          margin: '0 auto',
          padding: '12px 14px',
          borderRadius: '14px',
          background: 'var(--toast-bg)',
          color: 'var(--toast-fg)',
          border: '1px solid var(--toast-border)',
          borderLeft: '4px solid var(--app-accent)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>{nudge.title}</div>
          <div style={{ fontSize: '13px', opacity: 0.85, lineHeight: 1.4 }}>{nudge.body}</div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            flexShrink: 0,
            background: 'transparent',
            border: 'none',
            color: 'var(--toast-fg)',
            opacity: 0.55,
            fontSize: '18px',
            lineHeight: 1,
            cursor: 'pointer',
            padding: '2px 4px',
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
